import { useState } from 'react';
import { agreementsApi } from '@/modules/agreements/services/api';
import type { InteractiveSigningMethod } from './use-signing';
import type { SigningSessionResponse } from '@/api/schemas/agreements.types';

/** The two image types the API accepts for a signature asset. */
export type SignatureMimeType = 'image/png' | 'image/jpeg';

const MAX_BYTES = 2 * 1024 * 1024;

/**
 * The two-step signature upload handshake.
 *
 * The API issues a presigned target for a specific session, then the browser PUTs the bytes
 * directly. The API never receives the image through its own request body.
 *
 * The challenge comes from the signing session the SERVER opened — it is never generated here.
 * A client-invented value would not resolve to any session.
 *
 * Uploading an image does NOT complete the execution — the signer must still verify and sign.
 */
export function useSignatureUpload(input: {
  executionId: string;
  ensureSession: (method: InteractiveSigningMethod) => Promise<SigningSessionResponse>;
  signingMethod: InteractiveSigningMethod;
  mimeType: SignatureMimeType;
  onUploaded: (objectKey: string, mimeType: SignatureMimeType, byteSize: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);

  async function upload(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (file.size > MAX_BYTES) {
        throw new Error('The signature image must be 2 MiB or smaller.');
      }
      const session = await input.ensureSession(input.signingMethod);
      const target = await agreementsApi.issueSignatureUpload(input.executionId, {
        challenge: session.challenge,
        signingMethod: input.signingMethod,
        signatureMimeType: input.mimeType,
        signatureByteSize: file.size,
      });
      const response = await fetch(target.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': input.mimeType },
      });
      if (!response.ok) {
        throw new Error(`The upload was refused (${response.status}).`);
      }
      setUploadedKey(target.objectKey);
      input.onUploaded(target.objectKey, input.mimeType, file.size);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, uploadedKey, upload };
}
